import { create } from 'zustand';
import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  type MultiFactorError,
  type MultiFactorResolver,
  type User as FirebaseUser,
  type UserCredential,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { api, isApiError, getErrorMessage } from '@/lib/api';
import { requestMicroSurvey } from '@/lib/feedbackBus';
import { getFirebaseErrorMessage } from '@/lib/utils';
import type { UserRole, SeekerCategory, GuardianInfo, SponsorSector } from '@patrociconnect/shared';

interface AuthUser {
  uid: string;
  email: string;
  role: UserRole;
  status: string;
}

/** Corpo comum do cadastro (e-mail/senha e Google) — ver SignupProfile no backend */
export interface SignupProfile {
  role: 'seeker' | 'sponsor';
  name: string;
  location: { city: string; state: string; country?: string };
  acceptedTerms: true;
  acceptedPrivacy: true;
  seekerCategory?: SeekerCategory;
  /** YYYY-MM-DD */
  birthDate?: string;
  guardian?: GuardianInfo;
  sector?: SponsorSector;
}

export interface SignUpData extends SignupProfile {
  email: string;
  password: string;
}

/** Usuário autenticado no Google que ainda não tem cadastro na plataforma */
export interface PendingGoogleSignup {
  email: string;
  name: string | null;
}

interface ApiUser {
  uid?: string;
  id?: string;
  email: string;
  role: UserRole;
  status: string;
}

type GoogleStatusResponse =
  | { exists: true; user: ApiUser; profile: unknown }
  | { exists: false; email: string; name: string | null };

/** Login interrompido pelo Firebase pedindo o segundo fator (TOTP). */
export interface PendingMfaSignIn {
  resolver: MultiFactorResolver;
  method: 'password' | 'google';
}

type GoogleSignInResult = 'signed_in' | 'needs_signup' | 'cancelled' | 'mfa_required';

function isMfaRequiredError(error: unknown): error is MultiFactorError {
  return (error as { code?: string } | null)?.code === 'auth/multi-factor-auth-required';
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  error: string | null;
  isSigningUp: boolean;
  pendingGoogleSignup: PendingGoogleSignup | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: SignUpData, captchaToken: string | null) => Promise<void>;
  /** Retorna 'signed_in' ou 'needs_signup' (etapa "Complete seu cadastro") */
  signInWithGoogle: () => Promise<GoogleSignInResult>;
  pendingMfa: PendingMfaSignIn | null;
  /** Conclui o login com o código TOTP de 6 dígitos. Lança erro do Firebase se o código for inválido. */
  completeMfaSignIn: (code: string) => Promise<'signed_in' | 'needs_signup'>;
  cancelMfaSignIn: () => void;
  completeGoogleSignup: (data: SignupProfile, captchaToken: string | null) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

function toAuthUser(user: ApiUser): AuthUser {
  return {
    uid: user.uid || user.id || '',
    email: user.email,
    role: user.role,
    status: user.status,
  };
}

function isGoogleUser(firebaseUser: FirebaseUser): boolean {
  return firebaseUser.providerData.some((provider) => provider.providerId === 'google.com');
}

export const useAuthStore = create<AuthState>((set, get) => {
  onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
    // Durante o cadastro e o login com Google o próprio fluxo define o estado
    if (get().isSigningUp) {
      return;
    }

    if (!firebaseUser) {
      set({ user: null, isLoading: false, pendingGoogleSignup: null });
      return;
    }

    try {
      const response = await api.get<{ user: ApiUser; profile: unknown }>('/auth/me');
      set({ user: toAuthUser(response.user), isLoading: false, pendingGoogleSignup: null });
    } catch (error) {
      // Conta Google sem cadastro: mantém a sessão e leva à etapa de completar cadastro
      if (isApiError(error) && error.status === 404 && isGoogleUser(firebaseUser)) {
        set({
          user: null,
          isLoading: false,
          pendingGoogleSignup: { email: firebaseUser.email ?? '', name: firebaseUser.displayName },
        });
        return;
      }
      const message = getErrorMessage(error, 'Não foi possível carregar seus dados. Tente novamente.');
      await firebaseSignOut(auth);
      set({ user: null, isLoading: false, error: message, pendingGoogleSignup: null });
    }
  });

  /** Após o popup do Google (com ou sem MFA), verifica se a conta já tem cadastro. */
  const finishGoogleSignIn = async (credential: UserCredential): Promise<'signed_in' | 'needs_signup'> => {
    const idToken = await credential.user.getIdToken();
    const status = await api.get<GoogleStatusResponse>('/auth/google/complete', {
      requireAuth: false,
      bearerToken: idToken,
    });
    if (status.exists) {
      set({ user: toAuthUser(status.user), isLoading: false, isSigningUp: false, pendingGoogleSignup: null });
      return 'signed_in';
    }
    set({
      user: null,
      isLoading: false,
      isSigningUp: false,
      pendingGoogleSignup: { email: status.email, name: status.name },
    });
    return 'needs_signup';
  };

  return {
    user: null,
    pendingMfa: null,
    isLoading: true,
    error: null,
    isSigningUp: false,
    pendingGoogleSignup: null,

    signIn: async (email: string, password: string) => {
      set({ isLoading: true, error: null });
      try {
        await signInWithEmailAndPassword(auth, email, password);
        // onAuthStateChanged busca os dados via API
      } catch (error) {
        if (isMfaRequiredError(error)) {
          set({ isLoading: false, pendingMfa: { resolver: getMultiFactorResolver(auth, error), method: 'password' } });
          return;
        }
        const message = getFirebaseErrorMessage(error, 'Não foi possível fazer login. Tente novamente.');
        set({ error: message, isLoading: false });
        throw error;
      }
    },

    signUp: async (data: SignUpData, captchaToken: string | null) => {
      // Sem isLoading global: o AuthLayout desmontaria o formulário e perderia os dados digitados
      set({ error: null, isSigningUp: true });

      try {
        // O backend cria o usuário no Firebase Auth e devolve um customToken
        const response = await api.post<{ user: ApiUser; profile: unknown; token: string }>(
          '/auth/register',
          data,
          { requireAuth: false, captchaToken }
        );

        await signInWithCustomToken(auth, response.token);

        set({ user: toAuthUser(response.user), isLoading: false, isSigningUp: false });
        requestMicroSurvey('cadastro_concluido');
      } catch (error) {
        // Erros da API são exibidos pela página junto ao campo; aqui só libera o estado
        const message = isApiError(error)
          ? error.message
          : getFirebaseErrorMessage(error, 'Não foi possível criar sua conta. Tente novamente.');
        set({ error: isApiError(error) ? null : message, isSigningUp: false });
        throw error;
      }
    },

    signInWithGoogle: async () => {
      set({ error: null, isSigningUp: true });
      try {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        const credential = await signInWithPopup(auth, provider);
        return await finishGoogleSignIn(credential);
      } catch (error) {
        if (isMfaRequiredError(error)) {
          set({ isSigningUp: false, pendingMfa: { resolver: getMultiFactorResolver(auth, error), method: 'google' } });
          return 'mfa_required';
        }
        const code = (error as { code?: string }).code;
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
          set({ isSigningUp: false });
          return 'cancelled';
        }
        let message: string;
        if (isApiError(error) && error.status === 403) {
          message = error.message || 'Use uma conta Google com e-mail verificado.';
        } else if (isApiError(error) && error.status === 401) {
          message = 'Não foi possível validar sua conta Google. Tente novamente.';
        } else if (isApiError(error)) {
          message = error.message;
        } else {
          message = getFirebaseErrorMessage(error, 'Não foi possível entrar com o Google. Tente novamente.');
        }
        await firebaseSignOut(auth).catch(() => undefined);
        set({ error: message, isSigningUp: false, isLoading: false, pendingGoogleSignup: null });
        return 'cancelled';
      }
    },

    completeGoogleSignup: async (data: SignupProfile, captchaToken: string | null) => {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        set({ pendingGoogleSignup: null });
        throw new Error('Sua sessão com o Google expirou. Entre novamente.');
      }
      const idToken = await firebaseUser.getIdToken();
      const response = await api.post<{ created: boolean; user: ApiUser | null; profile: unknown }>(
        '/auth/google/complete',
        data,
        { requireAuth: false, bearerToken: idToken, captchaToken }
      );
      if (!response.user) {
        throw new Error('Não foi possível concluir o cadastro. Tente novamente.');
      }
      set({ user: toAuthUser(response.user), pendingGoogleSignup: null, isLoading: false });
      requestMicroSurvey('cadastro_concluido');
    },

    completeMfaSignIn: async (code: string) => {
      const pending = get().pendingMfa;
      if (!pending) throw new Error('Sua tentativa de login expirou. Entre novamente.');
      const totpHint = pending.resolver.hints.find((hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID);
      if (!totpHint) {
        throw new Error('Sua conta usa um segundo fator não suportado por este app. Fale com o suporte.');
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code);
      if (pending.method === 'password') {
        await pending.resolver.resolveSignIn(assertion);
        // onAuthStateChanged busca os dados via API
        set({ pendingMfa: null });
        return 'signed_in';
      }
      // Google: o próprio fluxo define o estado (como em signInWithGoogle)
      set({ isSigningUp: true });
      try {
        const credential = await pending.resolver.resolveSignIn(assertion);
        set({ pendingMfa: null });
        return await finishGoogleSignIn(credential);
      } catch (error) {
        if (isApiError(error)) {
          // Falha ao validar a conta na API: encerra a sessão Google, como no fluxo sem MFA
          await firebaseSignOut(auth).catch(() => undefined);
          set({ isSigningUp: false, pendingMfa: null, error: error.message || 'Não foi possível entrar com o Google. Tente novamente.' });
        } else {
          set({ isSigningUp: false });
        }
        throw error;
      }
    },

    cancelMfaSignIn: () => set({ pendingMfa: null, error: null }),

    signOut: async () => {
      try {
        await firebaseSignOut(auth);
      } finally {
        set({ user: null, pendingGoogleSignup: null });
      }
    },

    clearError: () => set({ error: null }),
  };
});
